import {
  Batch,
  ConsumableBatchType,
  DistributedListItem,
  PatientTherapySequence,
  PatientTherapySequenceMedicine,
  RestVialItem,
  SubstanceBatchTypes,
} from '@cypro/engine/schema'
import {convertAmountToUnit} from '@cypro/engine/utils'
import {Decimal} from 'decimal.js'
import {EMPTY_CONSUMABLES_CATEGORIES} from '@/modules/products/consumables/constants'

export function isUsageEnough(
  order: {batchId: string; quantity: number; sharedWith: string[]}[],
  batches: Batch[],
  required: {[key: string]: RequiredAmount},
  type: SubstanceBatchTypes | ConsumableBatchType = 'activeSubstances',
  allowedSolvents: string[] = []
) {
  const hasSharedBatches = order.some(b => b.sharedWith.length > 0)
  const usedBatches = batches.filter(b => order.some(ob => ob.batchId === b.id))
  const isUsageEnough: {[key: string]: {name: string; enough: boolean}} = {}
  for (const [key, value] of Object.entries(required)) {
    let usedAmount = new Decimal(0)
    let conversionError: string | undefined
    const batch = usedBatches.filter(b => {
      switch (type) {
        case 'activeSubstances':
          return b.tradeName?.prescribedNameId === key
        case 'carrier':
          return b.tradeName?.medicationProfile?.drugCatalogItemId === key
        case 'solvents':
          return allowedSolvents.includes(b.tradeName?.medicationProfile?.drugCatalogItemId || '')
        case 'consumables':
          return b.consumableId === key
        default:
          return false
      }
    })

    for (const b of batch) {
      let strengthOfBatch: Decimal

      if (type === 'consumables') {
        strengthOfBatch = new Decimal(1)
      } else if (type === 'carrier' || type === 'solvents') {
        // Carrier and solvent requirements are volumes (ml); strengthValue on these is a concentration (e.g. 0.9 %).
        strengthOfBatch = new Decimal(b.tradeName?.volumeValue || 0)
      } else {
        // the strength is in the trade name's strength unit, the required amount in the dose unit (e.g. 1 g vials for 1,198 mg)
        try {
          if (b.tradeName?.strengthValue == null) throw new Error('The trade name has no strength.')
          strengthOfBatch = convertAmountToUnit({
            amount: b.tradeName.strengthValue,
            from: b.tradeName.strengthUnit?.ucumCode,
            to: value.unit,
          })
        } catch (error) {
          conversionError = error instanceof Error ? error.message : String(error)
          strengthOfBatch = new Decimal(0)
        }
      }

      // an order can list one batch on several rows (e.g. × 2 plus a shared × 1): every row counts (CY-300)
      const quantityUsed = order.filter(ob => ob.batchId === b.id).reduce((sum, ob) => sum + (ob.quantity || 0), 0)
      const amountUsedBatch = strengthOfBatch.mul(quantityUsed)
      usedAmount = usedAmount.add(amountUsedBatch)
    }

    // a vial that can't be expressed in the dose unit never counts towards the dose
    const enough = !conversionError && usedAmount.greaterThanOrEqualTo(value.amount)
    isUsageEnough[key] = {
      name: value.name,
      enough,
    }

    if (!enough) {
      // TODO: remove once the prod "insufficient amount" issue is diagnosed
      console.warn('[picking-list] insufficient amount', {
        type,
        requiredKey: key,
        requiredName: value.name,
        requiredAmount: value.amount.toString(),
        requiredUnit: value.unit,
        usedAmount: usedAmount.toString(),
        conversionError,
        allowedSolvents: type === 'solvents' ? allowedSolvents : undefined,
        countedBatches: batch.map(b => ({
          batchId: b.id,
          lotNumber: b.lotNumber,
          tradeName: b.tradeName?.name,
          strengthValue: b.tradeName?.strengthValue,
          strengthUnitId: b.tradeName?.strengthUnitId,
          strengthUnit: b.tradeName?.strengthUnit?.ucumCode,
          volumeValue: b.tradeName?.volumeValue,
          quantityUsed: order.filter(ob => ob.batchId === b.id).reduce((sum, ob) => sum + (ob.quantity || 0), 0),
        })),
        notCountedBatches: usedBatches
          .filter(b => !batch.includes(b))
          .map(b => ({
            batchId: b.id,
            lotNumber: b.lotNumber,
            tradeName: b.tradeName?.name,
            prescribedNameId: b.tradeName?.prescribedNameId,
            drugCatalogItemId: b.tradeName?.medicationProfile?.drugCatalogItemId,
            consumableId: b.consumableId,
          })),
        selectedBatchIdsMissingFromLoadedBatches: order
          .map(ob => ob.batchId)
          .filter(id => !batches.some(b => b.id === id)),
      })
    }
  }

  const outOfStock: {name: string; enough: boolean}[] = Object.values(isUsageEnough).filter(item => !item.enough)
  return {hasSharedBatches, isUsageEnough, outOfStock, batchIds: usedBatches.map(b => b.id)}
}

export type CarrierContainer = 'PREFILLED' | 'EMPTY'

/** The cleanroom fills an empty container (AD with an empty bag, pump or cassette on a drug vial's trade name); otherwise the drug goes into a pre-filled bag. The server applies the same rule. */
export function getOrderCarrierContainer(
  volumeMode: string | null | undefined,
  drugBatchIds: string[],
  batches: Batch[]
): CarrierContainer {
  if (volumeMode === 'IN') return 'PREFILLED'
  const hasEmptyContainer = batches
    .filter(b => drugBatchIds.includes(b.id))
    .some(b =>
      (b.tradeName?.consumables ?? []).some(
        c => c.consumable?.category != null && EMPTY_CONSUMABLES_CATEGORIES.includes(c.consumable.category)
      )
    )
  return hasEmptyContainer ? 'EMPTY' : 'PREFILLED'
}

/**
 * Carrier bags belong to one preparation: a bag shared with another order never counts.
 * Pre-filled: one bag of the prescribed volume or more. Empty container: bags that together hold the volume.
 */
export function isCarrierUsageEnough(
  order: {batchId: string; quantity: number; sharedWith: string[]}[],
  batches: Batch[],
  carrierDrugId: string,
  required: RequiredAmount,
  container: CarrierContainer
) {
  const hasSharedBatches = order.some(b => b.sharedWith.length > 0)
  const usedBatches = batches.filter(b => order.some(ob => ob.batchId === b.id))
  const ownBags = order
    .filter(ob => ob.sharedWith.length === 0 && ob.quantity > 0)
    .flatMap(ob => {
      const b = usedBatches.find(batch => batch.id === ob.batchId)
      if (b?.tradeName?.medicationProfile?.drugCatalogItemId !== carrierDrugId) return []
      return [{batchId: ob.batchId, quantity: ob.quantity, volume: new Decimal(b.tradeName?.volumeValue || 0)}]
    })

  const bagCount = ownBags.reduce((sum, bag) => sum + bag.quantity, 0)
  const totalVolume = ownBags.reduce((sum, bag) => sum.add(bag.volume.mul(bag.quantity)), new Decimal(0))
  // no prescribed volume means no carrier to pick, as before (the server allocates none either)
  const enough =
    required.amount.lessThanOrEqualTo(0) ||
    (container === 'PREFILLED'
      ? bagCount === 1 && ownBags[0] != null && ownBags[0].volume.greaterThanOrEqualTo(required.amount)
      : totalVolume.greaterThanOrEqualTo(required.amount))

  if (!enough) {
    console.warn('[picking-list] insufficient carrier', {
      container,
      carrierDrugId,
      requiredName: required.name,
      requiredVolumeMl: required.amount.toString(),
      ownBags: ownBags.map(bag => ({...bag, volume: bag.volume.toString()})),
      sharedBags: order.filter(ob => ob.sharedWith.length > 0).map(ob => ob.batchId),
    })
  }

  const isUsageEnough = {[carrierDrugId]: {name: required.name, enough}}
  const outOfStock = enough ? [] : [{name: required.name, enough}]
  return {hasSharedBatches, isUsageEnough, outOfStock, batchIds: usedBatches.map(b => b.id)}
}

export type SolventRequirement = {solutionDrugId: string; name: string; amountMl: number}

/** Solvent volume needed per solvent drug: each reconstituted vial takes every solvent row of its trade name. */
export function buildSolventRequirements(
  order: {batchId: string; quantity: number}[],
  batches: Batch[]
): SolventRequirement[] {
  const totals = new Map<string, {name: string; amount: Decimal}>()
  for (const {batchId, quantity} of order) {
    const tradeName = batches.find(b => b.id === batchId)?.tradeName
    if (!tradeName?.reconstitutionRequired) continue
    for (const solvent of tradeName.reconstitutionSolvents ?? []) {
      const amount = new Decimal(solvent.solventVolume ?? 0).mul(quantity)
      const existing = totals.get(solvent.solutionDrugId)
      totals.set(solvent.solutionDrugId, {
        name: existing?.name ?? solvent.solutionDrug?.name ?? '',
        amount: existing ? existing.amount.add(amount) : amount,
      })
    }
  }
  return [...totals.entries()]
    .map(([solutionDrugId, {name, amount}]) => ({solutionDrugId, name, amountMl: amount.toNumber()}))
    .toSorted((a, b) => a.name.localeCompare(b.name))
}

/** Solvent requirements across picking-list orders: their new drug vials only, since a rest vial is already a solution. */
export function buildOrdersSolventRequirements(
  orders: Pick<DistributedListItem, 'activeSubstances' | 'restVials'>[],
  batches: Batch[]
): SolventRequirement[] {
  // a shared vial is listed under every order that shares it but reconstituted once, so count it
  // once per batch and sharing group, as getAggregatedBatchInfo does (CY-232)
  const seenSharedKeys = new Set<string>()
  const vials = orders
    .flatMap(order => order.activeSubstances.batches)
    .filter(b => {
      if (b.sharedWith.length === 0) return true
      const key = `${b.batchId}:${[...b.sharedWith].toSorted().join(',')}`
      if (seenSharedKeys.has(key)) return false
      seenSharedKeys.add(key)
      return true
    })
  return buildSolventRequirements(vials, batches)
}

export function buildRequiredSolvents(
  order: {batchId: string; quantity: number; sharedWith: string[]}[],
  batches: Batch[]
) {
  const result: {[key: string]: {amount: Decimal; name: string}} = {}
  const allowedSolvents: string[] = []
  let needReconstitution = false
  for (const ord of order) {
    const usedBatches = batches.filter(b => b.id === ord.batchId)
    const needReconstitutionBatches = usedBatches.filter(b => b.tradeName?.reconstitutionRequired)
    if (needReconstitutionBatches.length <= 0) {
      result[ord.batchId] = {amount: new Decimal(0), name: ''}
      continue
    }

    needReconstitution = true

    allowedSolvents.push(
      ...usedBatches.flatMap(b => b.tradeName?.reconstitutionSolvents?.map(s => s.solutionDrugId) || [])
    )
    for (const batch of needReconstitutionBatches) {
      // a trade name can list several solvents, each with its own volume; the
      // total solvent volume per vial is the sum across all solvent rows
      const solventVolume =
        batch.tradeName?.reconstitutionSolvents?.reduce((acc, s) => acc + (s.solventVolume || 0), 0) || 0
      const strengthOfBatch = new Decimal(solventVolume)
      const quantityUsed = ord.quantity
      const amountUsedBatch = strengthOfBatch.mul(quantityUsed)
      if (result[ord.batchId]) {
        result[ord.batchId] = {
          amount: result[ord.batchId].amount.add(amountUsedBatch),
          name: result[ord.batchId].name,
        }
      } else {
        // label as "<solvent(s)> (<trade name>)" so a shortage names the solvent to pick
        const tradeNameName = batch.tradeName?.name || ''
        const solventNames = (batch.tradeName?.reconstitutionSolvents ?? [])
          .map(s => s.solutionDrug?.name)
          .filter(Boolean)
          .join(', ')
        result[ord.batchId] = {
          amount: amountUsedBatch,
          name: solventNames ? `${solventNames} (${tradeNameName})` : tradeNameName,
        }
      }
    }
  }
  return {result, allowedSolvents, needReconstitution}
}

/** Names of the required solvent drugs that no trade name maps to, so no batch of them can ever be picked. */
export function findSolventsWithoutTradeName(
  solventDrugIds: string[],
  batches: Batch[],
  tradeNames: readonly {readonly medicationProfile?: {readonly drugCatalogItemId: string} | null}[]
): string[] {
  const drugsWithTradeName = new Set(tradeNames.map(tn => tn.medicationProfile?.drugCatalogItemId))
  const names = new Map<string, string>()
  for (const drugId of solventDrugIds) {
    if (drugsWithTradeName.has(drugId) || names.has(drugId)) continue
    const solvent = batches
      .flatMap(b => b.tradeName?.reconstitutionSolvents ?? [])
      .find(s => s.solutionDrugId === drugId)
    names.set(drugId, solvent?.solutionDrug?.name || drugId)
  }
  return [...names.values()]
}

export function buildRequiredConsumables(
  order: {batchId: string; quantity: number; sharedWith: string[]}[],
  batches: Batch[]
) {
  const result: {[key: string]: {amount: Decimal; name: string}} = {}
  for (const ord of order) {
    const usedBatches = batches.filter(b => b.id === ord.batchId)
    for (const batch of usedBatches) {
      const requiredConsumables = batch.tradeName?.consumables || []

      for (const consumable of requiredConsumables) {
        if (result[consumable.consumableId]) {
          result[consumable.consumableId] = {
            amount: result[consumable.consumableId].amount.add(new Decimal(ord.quantity || 0)),
            name: consumable.consumable?.name || '',
          }
        } else {
          result[consumable.consumableId] = {
            amount: new Decimal(ord.quantity || 0),
            name: consumable.consumable?.name || '',
          }
        }
      }
    }
  }
  return result
}

interface RequiredAmount {
  amount: Decimal
  name: string
  /** dose unit of an active substance amount (UCUM code, e.g. mg); vial strengths are converted into it */
  unit?: string | null
}

/** Dose unit of a medicine's totalAmount, read from its prescribed name's substance (the server reads the same). */
export function getMedicineDoseUnit(medicine: PatientTherapySequenceMedicine): string | null {
  const drug = medicine.prescribedName?.drugCatalogItem
  return (
    drug?.activeSubstanceDetails?.doseUnit?.ucumCode ?? drug?.supportiveMedicationDetails?.doseUnit?.ucumCode ?? null
  )
}

/** Amount of the substance the order's rest vials already cover, in the dose unit; a rest vial whose amount can't be converted covers nothing. */
export function getRestVialCoveredAmount(
  restVials: readonly RestVialItem[],
  batches: Batch[],
  prescribedNameId: string,
  doseUnit: string | null | undefined
): Decimal {
  return restVials.reduce((sum, rv) => {
    if (rv.amount == null || !rv.batchId) return sum
    const tradeName = batches.find(b => b.id === rv.batchId)?.tradeName
    if (tradeName?.prescribedNameId !== prescribedNameId) return sum
    try {
      // the rest vial amount is in its trade name's strength unit
      return sum.add(convertAmountToUnit({amount: rv.amount, from: tradeName.strengthUnit?.ucumCode, to: doseUnit}))
    } catch (error) {
      console.error('[picking-list] rest vial not counted: amount cannot be converted into the dose unit', {
        restVialId: rv.restVialId,
        amount: rv.amount,
        strengthUnit: tradeName.strengthUnit?.ucumCode,
        doseUnit,
        error,
      })
      return sum
    }
  }, new Decimal(0))
}

export interface PrescriptionRequiredAmounts {
  prescribedNameIds: string[]
  requiredPrescribedNameAmounts: {[key: string]: RequiredAmount}
  requiredCarrierAmount: RequiredAmount
}

export function getRequiredAmountForPrescriptions(prescriptions: PatientTherapySequence[]) {
  const result: Record<string, PrescriptionRequiredAmounts> = {}
  for (const prescription of prescriptions) {
    const prescribedNameIds: string[] = []
    const requiredPrescribedNameAmounts: {[key: string]: RequiredAmount} = {}
    const requiredCarrierAmount = prescription.carrierSolutionId
      ? {name: prescription.carrierSolution?.name || '', amount: new Decimal(prescription.volume || 0)}
      : {name: '', amount: new Decimal(0)}
    for (const medicine of prescription?.medicine || []) {
      if (medicine.prescribedNameId) {
        prescribedNameIds.push(medicine.prescribedNameId)
        if (requiredPrescribedNameAmounts[medicine.prescribedNameId] && medicine.totalAmount) {
          requiredPrescribedNameAmounts[medicine.prescribedNameId] = {
            amount: requiredPrescribedNameAmounts[medicine.prescribedNameId].amount.add(medicine.totalAmount),
            name: medicine.prescribedName?.name || '',
            unit: getMedicineDoseUnit(medicine),
          }
        } else {
          requiredPrescribedNameAmounts[medicine.prescribedNameId] = {
            amount: new Decimal(medicine.totalAmount || 0),
            name: medicine.prescribedName?.name || '',
            unit: getMedicineDoseUnit(medicine),
          }
        }
      }
    }
    result[prescription.id] = {
      prescribedNameIds,
      requiredPrescribedNameAmounts,
      requiredCarrierAmount,
    }
  }
  return result
}
